import $ from 'jquery';
import {
    Chart,
    Colors,
    DoughnutController,
    ArcElement,
    LineController,
    LineElement,
    PointElement,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

Chart.register(
    DoughnutController,
    Colors,
    ArcElement,
    LineController,
    LineElement,
    PointElement,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
);

$(function () {

    /* =========================
     * CONSTANTS
     * ========================= */

    const defaultType = 'bar',
        defaultOptionsScalePosition = 'left',
        defaultDrawOnChartArea = true,
        defaultLineTension = 0,
        defaultBorderWidth = 2,
        defaultDoughnutHoverOffset = 4,
        defaultLegendHoverFontColor = '#000',
        pieDoughnutLegendClickHandler = Chart.overrides.doughnut.plugins.legend.onClick;

    const lineColours = {
        0: {
            pointBorderColor: 'rgba(54,162,235,1)',
            pointHoverRadius: 4,
            pointHoverBackgroundColor: 'rgba(200,99,132,1)',
            pointHitRadius: 10
        },
        1: {
            pointBorderColor: 'rgba(200,99,132,1)',
            pointHoverRadius: 4,
            pointHoverBackgroundColor: 'rgba(2,117,216,1)',
            pointHitRadius: 10
        }
    };

    /* =========================
     * FORMATTERS
     * ========================= */

    let formats = {},
        numberFormat;

    // ALL real currencies from your DB (deduplicated, normalized)
    const CURRENCIES = [
        'AED', 'AFN', 'ARS', 'AUD', 'BMD', 'BND', 'BRL', 'CAD', 'CHF', 'CLP', 'CNY', 'COP',
        'DKK', 'EUR', 'GBP', 'HKD', 'IDR', 'ILS', 'INR', 'JPY', 'KRW', 'KZT', 'MMK', 'MXN',
        'MYR', 'NZD', 'PEN', 'PHP', 'RUB', 'SEK', 'SGD', 'STD', 'TRY', 'TTD', 'TWD',
        'USD', 'USN', 'USS', 'VND', 'VUV', 'ZAR'
    ];

    function normalizeUnit(unit) {
        if (!unit) return '';
        return unit
            .replace('/shares', '')
            .replace('shares/', '')
            .replace('/item', '')
            .replace('/warrant', '')
            .replace('/Mcf', '')
            .replace('/EUR', '')
            .trim();
    }

    function formatValue(value, unit, precision) {
        if (value === null || value === undefined) return '';

        let formatter;
        const normalizedUnit = normalizeUnit(unit);

        if (CURRENCIES.includes(normalizedUnit) && formats[normalizedUnit]) {
            if ((Math.abs(value) > 0 && Math.abs(value) < 100) || precision === 'precise') {
                formatter = formats[normalizedUnit].precise;
            } else {
                formatter = formats[normalizedUnit].human;
            }
        }
        else if (formats[normalizedUnit]) {
            formatter = formats[normalizedUnit];
        }
        else {
            formatter = numberFormat;
        }

        return formatter.format(value);
    }

    function isset(object) {
        return typeof object !== 'undefined';
    }

    /* =========================
     * CHART FACTORY
     * ========================= */

    function mychart(canvasId, chartData) {

        if (!chartData?.length || !chartData[0].data?.length) return;

        const canvas = document.querySelector(`#${canvasId}`);
        if (!canvas) return;

        let config = {
            type: chartData[0].type,
            data: {
                labels: chartData[0].labels,
                datasets: []
            },
            options: {
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: chartData.length > 1 || chartData[0].type === 'doughnut',
                        position: chartData[0].type === 'doughnut' ? 'left' : 'top',
                        onHover(e, legendItem) {
                            e.native.target.style.cursor = 'pointer';
                            legendItem.fontColor = defaultLegendHoverFontColor;
                            this.chart.render();
                        },
                        onLeave(e, legendItem) {
                            e.native.target.style.cursor = 'default';
                            legendItem.fontColor = Chart.defaults.color;
                            this.chart.render();
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label(e) {
                                const value = isset(e.parsed.y) ? e.parsed.y : e.parsed;
                                const dataset = chartData[e.datasetIndex];
                                let label = dataset.dataset_label || '';
                                if (label) label += ': ';
                                return label + formatValue(value, dataset.unit, dataset.precision);
                            }
                        }
                    }
                },
                scales: {}
            }
        };

        if (['pie', 'doughnut'].includes(chartData[0].type)) {
            config.options.plugins.legend.onClick = function (e, legendItem, legend) {
                if (isset(chartData[0].urls?.[legendItem.index])) {
                    window.location = chartData[0].urls[legendItem.index];
                } else {
                    pieDoughnutLegendClickHandler(e, legendItem, legend);
                }
            };
        }

        config.data.datasets = chartData.map((dataset, index) => {
            let result = {
                label: dataset.dataset_label,
                data: dataset.data,
                backgroundColor: dataset.background_colors ?? null,
                borderWidth: dataset.border_width ?? defaultBorderWidth,
                normalized: true,
                type: dataset.type ?? defaultType,
                yAxisID: 'y' + index,
                lineTension: defaultLineTension,
                order: chartData.length - index
            };

            if (result.type === 'line' && lineColours[index]) {
                Object.assign(result, lineColours[index]);
            }

            if (result.type === 'doughnut') {
                result.hoverOffset = defaultDoughnutHoverOffset;
            }

            return result;
        });

        chartData.forEach((dataset, index) => {
            if (dataset.type !== 'doughnut') {
                config.options.scales['y' + index] = {
                    beginAtZero: true,
                    position: dataset.scale_position ?? defaultOptionsScalePosition,
                    ticks: {
                        callback(value) {
                            return ' ' + formatValue(value, dataset.unit, dataset.precision) + ' ';
                        }
                    },
                    grid: {
                        drawOnChartArea: dataset.draw_on_chart_area ?? defaultDrawOnChartArea
                    }
                };
            }
        });

        return new Chart(canvas, config);
    }

    /* =========================
     * INIT FORMATTERS
     * ========================= */

    Chart.defaults.font.family = 'IBM Plex Sans';

    if ('Intl' in window && Intl.NumberFormat) {
        numberFormat = Intl.NumberFormat('en', { notation: 'compact' });

        formats['%'] = Intl.NumberFormat('en', {
            style: 'unit',
            unit: 'percent',
            maximumSignificantDigits: 3
        });

        CURRENCIES.forEach(currency => {
            formats[currency] = {
                human: Intl.NumberFormat('en', {
                    style: 'currency',
                    currency,
                    notation: 'compact'
                }),
                precise: Intl.NumberFormat('en', {
                    style: 'currency',
                    currency
                })
            };
        });
    } else {
        numberFormat = { format: e => e };
        formats['%'] = { format: e => e };
        CURRENCIES.forEach(currency => {
            formats[currency] = {
                human: { format: e => e },
                precise: { format: e => e }
            };
        });
    }

    /* =========================
     * BOOTSTRAP CHARTS
     * ========================= */

    window.mychart = mychart;

    window.investorChartData && mychart('investor-chart', [investorChartData]);

    window.securityChartData && (
        mychart('security-shares-chart', [securityChartData.shares, securityChartData.price]),
        mychart('security-value-chart', [securityChartData.value]),
        mychart('security-investors-chart', [securityChartData.investors])
    );

    window.investorSecurityChartData && (
        mychart('investor-security-shares-chart', [investorSecurityChartData.shares, investorSecurityChartData.price]),
        mychart('investor-security-value-chart', [investorSecurityChartData.value])
    );

    window.securityCompareChartData && (
        mychart('security-compare-investors-chart', [securityCompareChartData.investors]),
        mychart('security-compare-value-chart', [securityCompareChartData.value]),
        mychart('security-compare-shares-chart', [securityCompareChartData.shares])
    );

    if (window.tagChartsData) {
        Object.values(window.tagChartsData).forEach(frequencies => {
            const unitKey = frequencies._unit_key;
            Object.entries(frequencies).forEach(([frequency, datasets]) => {
                if (frequency === '_unit_key') return;

                datasets.value?.data?.length &&
                    mychart(`${unitKey}-${frequency}-tag-value-chart`, [datasets.value]);

                datasets.ttm?.data?.length &&
                    mychart(`${unitKey}-${frequency}-ttm-chart`, [datasets.ttm]);

                datasets.value_diff_ratio?.data?.length &&
                    mychart(`${unitKey}-${frequency}-tag-change-chart`, [datasets.value_diff_ratio]);
            });
        });
    }

});
